import React, { useEffect, memo } from 'react';
import { css } from 'styled-components';
import { checkValue, LightenDarkenColor, localization } from 'src/components/common/helper';
import { useStores } from 'src/utils/useStore';
// Components
import { Typo, FigureComponent } from 'src/components/common/ui';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import { UitkButtonSize } from 'uitk-react-button';
import PageLoader from 'src/components/common/modules/loader/page-loader';
import { getDeviceImage } from 'src/components/common/theme/component';
import Layout from 'src/components/common/theme/Layout';
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';

const DealCardView: React.FC = (props: any) => {
  const {
    image,
    turnOfGradient,
    title,
    subTitle,
    description,
    textColor,
    ctaColor,
    ctaTextColor,
    imageLabelText,
    dealCtaText,
    handleClick,
    isLoading,
    moreDealsLink,
    searchData,
    ctaText,
    uniquekey,
    checkInDateFormattedInputFormat,
    checkOutDateFormattedInputFormat,
    hotelCardInfo,
    isStaticTile,
    type = "",
    id
  } = props;

  const { width: windowWidth } = useWindowDimensions();
  const { context } = useStores();
  useEffect(() => {
    const parentClass = `#${id} .deal-fixtures-slider-container .slick-list .slick-track .slick-slide:not(.slick-cloned) .${uniquekey}`; 
    const upperContentAnimated = document.querySelector<HTMLElement>(`${parentClass} .deals-info-upper.animated`);
    if (!isLoading && checkValue(searchData, true) && !upperContentAnimated) { 
      setTimeout(() => {
        const dealsListContainer = document.querySelector<HTMLElement>(`${parentClass} .deals-list.hidden`);
        const dealsInfo = document.querySelector<HTMLElement>(`${parentClass} .deals-info`);
        const dealsInfoTitle = document.querySelector<HTMLElement>(`${parentClass} .deals-info .title`);
        const upperContent = document.querySelector<HTMLElement>(`${parentClass} .deals-info-upper`);
        if (dealsListContainer && dealsInfo && upperContent) {
          const dealsListContainerHeight = dealsListContainer.offsetHeight;
          const dealsInfoTop = dealsInfo.offsetTop;
          const dealsInfoHeight = dealsInfo.offsetHeight;
          const dealsInfoTitleHeight = dealsInfoTitle?.offsetHeight || 0;
          const dealsTopEdgeOffset = dealsInfoTop - (dealsInfoHeight / 2) - dealsInfoTitleHeight + 35;
          const upperContentHeight = upperContent.offsetHeight + dealsInfoTitleHeight;
          const upperContentTranslateHeight = (dealsTopEdgeOffset - ((395 - dealsListContainerHeight) / 2) + (upperContentHeight / 2) + 2);
          upperContent.animate([
            { transform: 'translateY(0px)' },
            { transform: `translateY(-${upperContentTranslateHeight}px)` }
          ], {
            duration: 1000,
            easing: "ease-in",
            fill: "forwards"
          })
          upperContent.classList.add("animated");
        }
      }, 10)
    }
  }, [searchData, isLoading]);

  useEffect(() => {
    const fixtureDeals = document.querySelectorAll<HTMLElement>(`#${id} .slick-slide .deals-info-upper`);
    if (!checkValue(fixtureDeals, true)) return null;
    fixtureDeals.forEach(deal => {
      const subInfo = deal.querySelector<HTMLElement>(`.sub-info`);
      const title = deal.querySelector<HTMLElement>(`.title`);
      if (subInfo && title) {
        title.style.bottom = `${subInfo.getBoundingClientRect().height + 5}px`
      }
    })
  }, [searchData, isLoading]);

  let offerDate = "";
  if (hotelCardInfo === "Date") {
    const options = {
      skeleton: 'dddMMMD',
      locale: context?.locale || 'en',
    };
    const checkInFormatted = localization.formatDate(
      new Date(
        `${checkInDateFormattedInputFormat?.year}/${checkInDateFormattedInputFormat?.month}/${checkInDateFormattedInputFormat?.day}`
      ),
      options
    );
    const checkOutFormatted = localization.formatDate(
      new Date(
        `${checkOutDateFormattedInputFormat?.year}/${checkOutDateFormattedInputFormat?.month}/${checkOutDateFormattedInputFormat?.day}`
      ),
      options
    );
    offerDate = `<span class="offer-date">${checkInFormatted} -</span> <span class="offer-date">${checkOutFormatted}</span>`;
  }

  let windowSize = '';
  if (windowWidth > 920) windowSize = 'desktop';
  else if (windowWidth > 575) windowSize = 'tablet';
  else windowSize = 'mobile';

  // Dynamic style overrides
  const moduleCss = () => {
    const buttonHoverColor = checkValue(ctaColor) ? LightenDarkenColor(ctaColor, -0.1) : '';
    return `
	${
    checkValue(textColor)
      ? `
		.title {
			color: ${textColor} !important;
		}
		.sub-info {
			color: ${textColor} !important;
		}
		.content {
			color: ${textColor} !important;
		}
		`
      : ''
  }
	${
    checkValue(ctaColor)
      ? `
        .deal-fixtures-cta {
          	background-color: ${ctaColor} !important;
        }
		.deal-fixtures-cta:hover {
			background-color: ${buttonHoverColor} !important;
			border-color: ${buttonHoverColor} !important;
		}
      `
      : ''
  }
	${
    checkValue(ctaTextColor)
      ? `
	.deal-fixtures-cta {
		color: ${ctaTextColor} !important;
	}
	`
      : ''
  }  
	`;
  };

  const figureProps = {
    src: getDeviceImage(image, windowSize),
    exClass: 'deal-fixtures-item--image',
    alt: '',
    copyRightProps: {},
    isGradientActive: turnOfGradient !== 'True',
    imageRatio: UitkFigureAspectRatioType.R16_9,
  };

  const primaryButtonProps: PrimaryButtonProps = {
    text: ctaText,
    tag: 'button',
    size: UitkButtonSize.LARGE,
    gaTracking: {
      eventCategory: '',
      eventAction: '',
      eventLabel: ``,
    },
    exClass: `deal-fixtures-cta ${isStaticTile === "True" && 'hide-cta'}`,
  };

  const dealInfo = (props) => (
    <a
      key={props.id}
      className="deal-container-link"
      href={props.redirectLink}
      target="_blank"
    >
      <div
        className={`deal-container
        ${props.slidePosistion}
        ${checkValue(dealCtaText) && checkValue(moreDealsLink) ? 'with-more-link' : 'without-more-link'}`}
      >
        <div className={`deal-container-left ${hotelCardInfo === "Date" ? "with-date" : ""}`}>
          <Typo.HTMLText tag="p" exClass="deal-title">
            {props?.name}
          </Typo.HTMLText>
          { (props.destinationInfo) && 
              <Typo.HTMLText tag="p" exClass="deal-subtitle">
                  {props.destinationInfo}
              </Typo.HTMLText>
          }
        </div>
        { props.availability ? 
          <div>
              <Typo.HTMLText tag="p" exClass="deal-price">
                  {props.price}
              </Typo.HTMLText>
              <Typo.HTMLText tag="p" exClass={`deal-price-info  ${hotelCardInfo === "Date" ? "date-container" : ""}`}>
                  {props.helpText}
              </Typo.HTMLText>
          </div>
        :
          <div>
              <Typo.HTMLText tag="p" exClass="deal-price-info">
                  {props.availabilityText}
              </Typo.HTMLText>
          </div> 
        }
      </div>
    </a>
  )

  const deals = () => (
    checkValue(searchData, true) && (
      <div className="deals-list-inner">
        {checkValue(imageLabelText) && <div className="deal-tag">{imageLabelText}</div>}
        {searchData.map((el: any, index) => {
          let slidePosistion = '';
          if (searchData.length === 1) slidePosistion = 'only-one';
          else if (index === searchData.length - 1) slidePosistion = 'last';
          else if (index === 0) slidePosistion = 'first';

          let dealsData = null;
          if (type === "campaign") {
            dealsData = {
              id: `${index}${el.name}`,
              name: el.name,
              slidePosistion,
              destinationInfo: el?.location,
              availability: true,
              price: el?.lodgingOffer?.price?.value,
              helpText: el?.lodgingOffer?.price?.qualifierText,
              availabilityText: "",
              redirectLink: el?.lodgingOffer?.actionLink.href
            }
          }
          else {
            dealsData = {
              id: el.id,
              name: el.name,
              slidePosistion,
              destinationInfo: el?.destinationInfo?.distanceFromMessaging || el?.neighborhood?.name,
              availability: el?.availability?.available,
              price: el?.price?.lead?.formatted,
              helpText: hotelCardInfo === "Date" ? offerDate : el?.price?.roomNightMessage,
              availabilityText: el?.availability?.text,
              redirectLink: el?.propertyDetailsLink?.uri?.value
            }
          }

          return dealInfo(dealsData);
        })}
        {checkValue(dealCtaText) && checkValue(moreDealsLink) && (
          <div className="more-deals">
            <a
              className="more-link"
              href={moreDealsLink}
              target="_blank"
            >
              {dealCtaText}
            </a>
          </div>
        )
        }
      </div>
    )
  );

  return (
    <Layout
      css={css`
        ${moduleCss()}
      `}
    >
      <div className={`deal-fixtures-item ${uniquekey}`}>
        <FigureComponent {...figureProps} />
        <div className="deals-info">
          <div className={`deals-info-upper`}>
            <Typo.HTMLText tag="h2" exClass="title">
              {title}
            </Typo.HTMLText>
            <Typo.HTMLText tag="p" exClass="sub-info">
              {subTitle}
            </Typo.HTMLText>
          </div>
          <div className={`deals-info-lower ${checkValue(searchData, true) ? 'set-animation' : ''}`}>
            <Typo.HTMLText tag="p" exClass="content">
              {description}
            </Typo.HTMLText>
            { ctaText && <PrimaryButton onClickEvent={handleClick} {...primaryButtonProps} /> }
          </div>
        </div>
        {isLoading && <PageLoader sectionLoader />}
        <div className={`deals-list ${checkValue(searchData, true) ? 'set-animation' : ''} `}>
          {deals()}
        </div>
        <div className={`deals-list hidden`}>
          {deals()}
        </div>
      </div>
    </Layout>
  );
};

export default memo(DealCardView);
